import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { FormatService } from '../../services/format/format.service';
import { ApiService } from '../../services/api/api.service';
import configJson from '../../../assets/config.json';

const SESSION = 'hatam-app';
const IS_API = configJson.apiUrl;

// menu key -> route path, in the order we'd prefer to land someone on after
// login when they don't have 'dashboard' itself. Keep in sync with
// app-routing.module.ts's `data.menu` values.
const MENU_ROUTES: {menu: string; path: string}[] = [
  {menu: 'dashboard', path: '/dashboard'},
  {menu: 'contentFeed', path: '/content-feed'},
  {menu: 'contentVideo', path: '/content-video'},
  {menu: 'user', path: '/user'},
  {menu: 'voucherList', path: '/voucher-list'},
  {menu: 'complaint', path: '/complaint'},
  {menu: 'audio', path: '/audio'},
  {menu: 'hafalan', path: '/hafalan'},
  {menu: 'murojaah', path: '/murojaah'},
  {menu: 'tilawah', path: '/tilawah'},
  {menu: 'faq', path: '/faq'},
  {menu: 'sahamqu', path: '/sahamqu'},
  {menu: 'subscribeVoucher', path: '/subscribe-voucher'},
  {menu: 'subscribeStore', path: '/subscribe-store'},
  {menu: 'subscriptionPlan', path: '/subscription-plan'},
  {menu: 'voucherBackground', path: '/voucher-background'},
  {menu: 'konfigurasi', path: '/konfigurasi'},
];

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isSession: any = null;

  constructor(
    private http: HttpClient,
    private formatService: FormatService,
    private apiService: ApiService,
    private toastr: ToastrService,
  ) {
    this.loadSession();
  }

  loadSession() {
    try{
      const sessionData = localStorage.getItem(btoa(btoa(SESSION)));
      if (sessionData) {
        this.isSession = atob(atob(sessionData));
        // sessions stored before the fix still carry the password hash — scrub them
        const parsed = JSON.parse(this.isSession);
        if (parsed && parsed.password !== undefined) {
          delete parsed.password;
          localStorage.setItem(btoa(btoa(SESSION)), btoa(btoa(JSON.stringify(parsed))));
          this.isSession = JSON.stringify(parsed);
        }
        this.isAuthenticated.next(true);
      } else {
        this.isAuthenticated.next(false);
      }
    }catch(error){
      console.log(error);
    }
  }
  getSession(){
    try{
      const sessionData = localStorage.getItem(btoa(btoa(SESSION)));
      if (sessionData) {
        this.isSession = atob(atob(sessionData));
        return JSON.parse(this.isSession);
      } else {
        return {};
      }
    }catch(error){
      console.log(error);
    }
  }
  sessionCek(){
    const sessionData = localStorage.getItem(btoa(btoa(SESSION)));
    if (sessionData) {
      return true;
    } else {
      return false;
    }
  }
  getToken(){
    try{
      const sessionData = localStorage.getItem(btoa(btoa(SESSION)));
      if (sessionData) {
        let token = JSON.parse(atob(atob(sessionData)));
        return token.token;
      } else {
        return {};
      }
    }catch(error){
      console.log(error);
    }
  }
  
  // merge profile changes into the stored session (token & other fields survive)
  updateSession(patch: any) {
    try {
      const current = this.getSession() || {};
      const merged = {...current, ...patch};
      localStorage.setItem(btoa(btoa(SESSION)), btoa(btoa(JSON.stringify(merged))));
    } catch (error) {
      console.log(error);
    }
  }

  isSuperAdmin(){
    return String(this.getSession().role) === '0';
  }

  // Super Admin bypasses the permission system entirely; everyone else needs
  // the menu key in their role's granted permissions (see login response).
  hasMenu(menuKey : string){
    if(this.isSuperAdmin()){ return true; }
    const permissions = this.getSession().permissions || [];
    return permissions.includes(menuKey);
  }

  isImpersonating(){
    return !!this.getSession().impersonated_by;
  }

  // where to send someone right after login (or when they land on a route
  // they no longer have access to) — the first menu their role actually grants.
  // null means they have no access anywhere and must not be let past /login.
  landingRoute(): string | null {
    if(this.isSuperAdmin()){ return '/dashboard'; }
    const found = MENU_ROUTES.find(m => this.hasMenu(m.menu));
    return found ? found.path : null;
  }

  private replaceSession(data : any){
    localStorage.setItem(btoa(btoa(SESSION)), btoa(btoa(JSON.stringify(data))));
    this.isAuthenticated.next(true);
  }

  impersonate(adminId : any): Observable<any> {
    return this.http.post(`${IS_API}admin/impersonate/${adminId}`, {}, {
      headers: new HttpHeaders({Authorization: `Bearer ${this.getToken()}`})
    }).pipe(
      map((data: any) => {
        if(data && data.status == 200){ this.replaceSession(data.data); }
        return data;
      })
    );
  }

  stopImpersonate(): Observable<any> {
    return this.http.post(`${IS_API}admin/stop-impersonate`, {}, {
      headers: new HttpHeaders({Authorization: `Bearer ${this.getToken()}`})
    }).pipe(
      map((data: any) => {
        if(data && data.status == 200){ this.replaceSession(data.data); }
        return data;
      })
    );
  }

  login(credentials: any): Observable<any> {
    // no BlockUI and no toasts here — the login page owns its own loading
    // state and shows failures inline, where the user's eyes already are
    return this.http.post(`${IS_API}login`, credentials).pipe(
      map((data: any) => {
        if (data && data.status == 200) {
          const session = {...data.data};
          delete session.password; // belt & braces — the API no longer sends it either
          localStorage.setItem(btoa(btoa(SESSION)), btoa(btoa(JSON.stringify(session))));
          this.isAuthenticated.next(true);
        }
        return data;
      })
    );
  }

  logout() {
    // this.apiService.methodGet('authLogout', this.isToken).subscribe(
    //   async (res: any) => {
    //     if (res.status.statusCode == 200) {
          this.isAuthenticated.next(false);
          localStorage.clear();
    //     }
    //   },
    //   async (response) => { console.log(response); }
    // );
  }
}